import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CircularProgress } from '../CircularProgress';
import { twMerge } from 'tailwind-merge';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The variant of the button */
  variant?: 'solid' | 'outlined' | 'plain' | 'soft';
  /** The color scheme of the button */
  color?: 'primary' | 'neutral' | 'danger' | 'success' | 'warning';
  /** The size of the button */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** The icon to display before the button text */
  startIcon?: IconProp;
  /** The icon to display after the button text */
  endIcon?: IconProp;
  /** Decorative element to display before the button text */
  startDecorator?: React.ReactNode;
  /** Decorative element to display after the button text */
  endDecorator?: React.ReactNode;
  /** Whether the button is full width */
  fullWidth?: boolean;
  /** Whether the button is circular */
  circular?: boolean;
  /** Whether the button has a loading spinner */
  loadingPosition?: 'start' | 'end' | 'center';
  /**
   * The children of the button.
   * Note: If text truncation is needed, wrap the text content in a span with appropriate truncation classes:
   * <Button><span className="truncate">Long text content...</span></Button>
   */
  children?: React.ReactNode;
  /** Additional className for the button */
  className?: string;
  /** SX props for the button */
  sx?: React.CSSProperties;
  /** The onClick handler for the button */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** The onKeyDown handler for the button */
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'solid',
      color = 'neutral',
      size = 'md',
      loading = false,
      startIcon,
      endIcon,
      startDecorator,
      endDecorator,
      fullWidth = false,
      circular = false,
      disabled,
      className = '',
      children,
      sx = {},
      onClick,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick?.(event as unknown as React.MouseEvent<HTMLButtonElement>);
      }
      onKeyDown?.(event);
    };

    const baseStyles =
      'inline-flex items-center justify-center font-semibold transition-all duration-200 select-none';

    const sizeStyles = {
      sm: circular
        ? 'w-8 h-8 min-w-[2rem] min-h-[2rem] p-0'
        : 'px-2 py-1 h-8 text-sm',
      md: circular
        ? 'w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] p-0'
        : 'px-3 py-2 h-10 text-sm',
      lg: circular
        ? 'w-12 h-12 min-w-[3rem] min-h-[3rem] p-0'
        : 'px-4 py-2 h-12 text-base',
    };

    const variantStyles = {
      solid: {
        primary:
          'bg-sky-500 text-white hover:bg-sky-600 active:bg-sky-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
        neutral:
          'bg-neutral-800 text-white hover:bg-neutral-900 active:bg-neutral-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-500',
        danger:
          'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500',
        success:
          'bg-green-600 text-white hover:bg-green-700 active:bg-green-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500',
        warning:
          'bg-yellow-500 text-white hover:bg-yellow-600 active:bg-yellow-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-500',
      },
      outlined: {
        primary:
          'border border-sky-300 text-sky-600 hover:bg-sky-50 active:bg-sky-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
        neutral:
          'border-neutral-300 border text-neutral-700 bg-white hover:!bg-neutral-100 active:!bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-500',
        danger:
          'border border-red-300 text-red-700 hover:bg-red-50 active:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500',
        success:
          'border border-green-300 text-green-700 hover:bg-green-50 active:bg-green-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500',
        warning:
          'border border-yellow-300 text-yellow-700 hover:bg-yellow-50 active:bg-yellow-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-500',
      },
      plain: {
        primary:
          'text-sky-600 hover:bg-sky-50 active:bg-sky-100 focus-visible:outline-none',
        neutral:
          'text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 focus-visible:outline-none',
        danger:
          'text-red-700 hover:bg-red-50 active:bg-red-100 focus-visible:outline-none',
        success:
          'text-green-700 hover:bg-green-50 active:bg-green-100 focus-visible:outline-none',
        warning:
          'text-yellow-700 hover:bg-yellow-50 active:bg-yellow-100 focus-visible:outline-none',
      },
      soft: {
        primary:
          'bg-sky-50 text-sky-600 hover:bg-sky-100 active:bg-sky-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
        neutral:
          'bg-neutral-50 text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-500',
        danger:
          'bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500',
        success:
          'bg-green-50 text-green-700 hover:bg-green-100 active:bg-green-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500',
        warning:
          'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 active:bg-yellow-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-500',
      },
    };

    const disabledStyles = {
      solid:
        'disabled:bg-neutral-100 disabled:text-neutral-400 disabled:border-none',
      outlined:
        'disabled:border-neutral-200 disabled:text-neutral-400 disabled:bg-transparent',
      plain: 'disabled:text-neutral-400 disabled:bg-transparent',
      soft: 'disabled:bg-neutral-100 disabled:text-neutral-400',
    };

    const roundedStyles = circular ? 'rounded-full' : 'rounded-lg';
    const widthStyles = fullWidth ? 'w-full' : '';

    const buttonClasses = twMerge(
      baseStyles,
      sizeStyles[size],
      variantStyles[variant][color],
      disabledStyles[variant],
      roundedStyles,
      widthStyles,
      'relative',
      className
    );

    const iconSizeClass =
      size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base';

    // Follow industry standards for button text alignment
    const hasStart = !!(startIcon || startDecorator);
    const hasEnd = !!(endIcon || endDecorator);
    const justifyClass =
      hasStart && hasEnd
        ? 'justify-between' // Both icons: space them apart, text fills middle
        : hasStart
        ? 'justify-start' // Start icon: text flows naturally after icon
        : hasEnd
        ? 'justify-between' // End icon: text left, icon right
        : 'justify-center'; // Text only: centered (industry standard)

    const contentClasses = twMerge(
      'flex items-center gap-3 w-full',
      justifyClass,
      loading ? 'invisible' : 'visible'
    );

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={buttonClasses}
        style={sx}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        {...props}
      >
        <span className={contentClasses}>
          {(startIcon || startDecorator) && (
            <span className="flex items-center gap-2 flex-none pl-1">
              {!loading && startDecorator && startDecorator}
              {!loading && startIcon && (
                <Icon icon={startIcon} className={iconSizeClass} />
              )}
            </span>
          )}
          <span
            className={
              hasStart && hasEnd ? 'flex-1 truncate min-w-0' : 'truncate'
            }
          >
            {children}
          </span>
          {(endIcon || endDecorator) && (
            <span className="flex items-center gap-2 flex-none">
              {!loading && endIcon && (
                <Icon icon={endIcon} className={iconSizeClass} />
              )}
              {!loading && endDecorator && endDecorator}
            </span>
          )}
        </span>
        {loading && (
          <span
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            data-testid="loading-spinner"
          >
            <CircularProgress size="sm" color="primary" />
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
